import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Push } from 'connected-react-router';
import useFilters from 'hooks/useFilters';
import { useTicket } from 'contexts/TicketContext';
import AGMessage from 'components/AGMessage/AGMessage';
import SearchInput from 'components/AGForm/Inputs/SearchInput/SearchInput';
import TicketCard from 'components/Dashboard/TicketCard/TicketCard';
import Button from 'components/Button/Button';
import Loader from 'components/Loader/Loader';
import Modal, { ModalRef } from 'components/Modal/Modal';
import ConfirmAction from 'components/ConfirmAction/ConfirmAction';
import styles from './TicketLookup.module.css';

type TicketLookupProps = {
    connectedPush: Push;
};

function TicketLookup({ connectedPush }: TicketLookupProps) {
    const [selectedTicketUUID, setSelectedTicketUUID] = useState('');
    const { getTicket, waiveTicket, loading, tickets, errorMessage, successMessage } = useTicket();
    const waiveTicketModal = useRef<ModalRef>();
    const { filters, urlUpdater } = useFilters<{ searchTerm: string }>({
        filterValidators: {
            searchTerm: function validator(currentValue: string | null): string {
                return currentValue || '';
            },
        },
        push: connectedPush,
    });

    const isValidTicketID = useMemo(() => filters.searchTerm.length >= 3, [filters.searchTerm]);

    function showModal(ticketUUID: string) {
        setSelectedTicketUUID(ticketUUID);
        waiveTicketModal.current?.openModal();
    }

    function closeModal() {
        setSelectedTicketUUID('');
        waiveTicketModal.current?.closeModal();
    }

    function onSearchTicket() {
        if (isValidTicketID) {
            getTicket(filters.searchTerm);
        }
    }

    function onWaiveTicket() {
        waiveTicket(selectedTicketUUID);
        waiveTicketModal.current?.closeModal();
        urlUpdater.searchTerm('');
        setSelectedTicketUUID('');
    }

    useEffect(() => {
        if (filters.searchTerm) onSearchTicket();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <div style={{ maxWidth: '600px' }}>
                {errorMessage && <AGMessage color="error">{errorMessage}</AGMessage>}
                {successMessage && <AGMessage color="success">{successMessage}</AGMessage>}

                <form
                    className={styles.form}
                    onSubmit={(e) => {
                        e.preventDefault();
                        onSearchTicket();
                    }}
                >
                    <SearchInput
                        value={filters.searchTerm}
                        onChange={(e) => urlUpdater.searchTerm(e.currentTarget.value)}
                        placeholder="Violation number or plate"
                        className={styles.searchInput}
                        autoFocus
                        type="text"
                        disabled={loading}
                        name="violation"
                    />
                    <Button
                        className={styles.lookupButton}
                        size="sm"
                        disabled={!isValidTicketID}
                        onClick={onSearchTicket}
                    >
                        Lookup
                    </Button>
                </form>
            </div>

            {loading ? (
                <Loader />
            ) : (
                <section className={styles.ticketGrid}>
                    {tickets?.map((ticket) => (
                        <TicketCard key={ticket.uuid} ticket={ticket} showModal={showModal} />
                    ))}
                </section>
            )}

            {/* Waive Ticket modal */}
            <Modal ref={waiveTicketModal} title="Are you sure you want to waive this violation?">
                <ConfirmAction onCancel={closeModal} onConfirm={onWaiveTicket} action="Waive" loading={loading}>
                    <p>Waiving a violation means a driver will not have to pay the fee.</p>
                </ConfirmAction>
            </Modal>
        </div>
    );
}
export default TicketLookup;
